import React from 'react'
import { original, produce } from 'immer'
import { z } from 'zod'
import styles from './WidgetConfiguration.module.css'

import { Dialog, DialogContent, DialogFooter, DialogHeader } from './Dialog'
import { Button } from 'reactstrap'
import { FormGroup } from './FormGroup'
import FormField, { FormFields } from './FormField'

import { ConfigDescription, ConfigurablePropsFromDescription, WidgetDescription } from '../helpers/widget'
import { allWidgetDefinitionsSorted } from './widgets'
import { DEFAULT_WIDGET_CONFIG } from './default-widget-config'
import { forEachEntry } from '../utils/typescript'
import { useDebounce } from '../hooks/use-debounce'
import { useFeatureFlags } from '../flags'

export type WidgetConfig
  = GeneralWidgetConfig
  & ConfigurablePropsFromDescription<AnyWidgetDefinition['variables']>

export const generalWidgetConfigSchema = z.object({
  id: z.string().optional(),
  widget: z.string().optional(),
  panelstyle: z.string().optional(),
  panelstyleExtra: z.string().optional(),
  titleMenubar: z.string().optional(),
  infoMenubar: z.string().optional(),
  colorPrimaryAction: z.boolean().optional(),
  showLoadingOverlay: z.boolean().optional(),
  syncWithUserActionsDisabled: z.boolean(),
  voteForUserActionsDisabledWhenLoading: z.boolean(),
  hidden: z.string().optional(),
  fontFamily: z.string().optional(),
  finalTopLeftBorderRadius: z.string().optional(),
  finalTopRightBorderRadius: z.string().optional(),
  finalBottomLeftBorderRadius: z.string().optional(),
  finalBottomRightBorderRadius: z.string().optional(),
  hiddenwhenparametersareempty: z.string().optional(),
  query: z.string().optional()
})

export type GeneralWidgetConfig = z.infer<typeof generalWidgetConfigSchema> & {
  // Not in zod because we'd need to repeat the type def twice to be able to use z.lazy(). Maybe reconsider later.
  areas?: (GeneralWidgetConfig | null)[]
}

type AnyWidgetDefinition = {
  label: string | JSX.Element,
  name: string,
  variables: ConfigDescription<any, any, any>[],
  /**
   * Optional: a widget may contribute extra config fields derived from the
   * current config (e.g. CodeWidget parses fields declared in its html page).
   */
  deriveExtraVariables?: (config: Record<string, unknown>) => ConfigDescription<any, any, any>[],
}

type State = {
  waitingToSaveAndClose: boolean
  configInput: WidgetConfig
  configOriginal: WidgetConfig
  feedback: {
    warning: Record<string, React.ReactNode>
    error: Record<string, React.ReactNode>
  },
}

export function WidgetConfiguration(props: {
  /**
   * Where this widget sits in the layout, e.g. "main.0". Stable across reloads
   * because it describes the app config rather than the render, which is what
   * lets per-field editor history find its way back to the right widget.
   */
  areaPath?: string
  configRendered: WidgetConfig
  widgetDefinition?: AnyWidgetDefinition
  close: () => void
  saveConfigRendered: () => void
  setConfigToRender: (config: WidgetConfig) => void
}) {
  const [state, setState] = React.useState<State>(() => ({
    waitingToSaveAndClose: false,
    feedback: { warning: {}, error: {} },
    configInput: props.configRendered,
    configOriginal: props.configRendered,
  }))

  React.useEffect(() => {
    if (!state.waitingToSaveAndClose) return
    if (props.configRendered !== state.configInput) return
    props.saveConfigRendered()
    props.close()
  }, [state.waitingToSaveAndClose, props.configRendered])

  // ref, because we want it instantly updated without need for reactivity
  const delayedUpdateEnabled = React.useRef<boolean>(true)

  const updateConfigField = (key: string, value: any) =>
    setState(produce(draft => {
      if (key === 'widget') {
        mutateWidgetTypeWithDefaults(draft.configInput, value)
      } else {
        draft.configInput = { ...draft.configInput, [key]: value }
      }
    }))

  useDebounce(() => {
    if (state.configInput === props.configRendered) return
    if (delayedUpdateEnabled.current) props.setConfigToRender(state.configInput)
  }, 200, [state.configInput])

  /*
   * Scopes a field's version history to one widget in one app.
   *
   * The app comes from the URL, which names the project and the viewer, so two
   * apps that happen to share a viewer path in different projects stay apart.
   *
   * The widget comes from its slot in the layout rather than from its config:
   * `id` is optional and hand-typed, and the widget type is shared by every
   * widget of that type, so either would hand two CodeWidgets in one split
   * layout the same history. A slot belongs to one widget and survives a
   * reload. The type rides along so that swapping the widget in a slot starts
   * a fresh history rather than inheriting the previous occupant's.
   */
  const appPath = window.location.pathname
  const widgetPath = `${props.areaPath ?? 'main'}:${state.configInput.widget ?? 'widget'}`
  const localHistoryKeyPrefix = `${appPath}:${widgetPath}`

  const selectableWidgetOptions = useSelectableWidgetOptions()

  const setConfigFeedback = (
    varName: string, type: 'valid' | 'warning' | 'error', message?: React.ReactNode,
  ) => setState(produce(draft => {
    switch (type) {
      case 'valid':
        delete draft.feedback.error[varName]
        delete draft.feedback.warning[varName]
        return
      case 'error':
        delete draft.feedback.warning[varName]
        draft.feedback.error[varName] = message
        return
      case 'warning':
        delete draft.feedback.error[varName]
        draft.feedback.warning[varName] = message
        return
      default:
        throw new Error('Oops should be unreachable, dev spaghetti. If this happens often, migrate (partially) to TypeScript')
    }
  }))

  const widgetDefinition =
    props.widgetDefinition ??
    allWidgetDefinitionsSorted.find(w => w.name === state.configInput.widget)

  /*
   * The static config fields merged with any a widget derives from its current
   * config (opt-in via deriveExtraVariables; empty for widgets that don't implement it).
   * Merged inside the memo so the array identity is stable across renders — otherwise
   * FormFields would rebuild its form parts on every render.
   */
  const descriptions = React.useMemo(() => {
    const definition = widgetDefinition as AnyWidgetDefinition | undefined
    const staticVariables = widgetDefinition?.variables ?? []
    const derivedVariables = definition?.deriveExtraVariables?.(state.configInput as Record<string, unknown>) ?? []
    return [...staticVariables, ...derivedVariables] as ReadonlyArray<ConfigDescription<any, any, any, any>>
  }, [widgetDefinition, state.configInput])

  const cancel = () => {
    delayedUpdateEnabled.current = false
    props.setConfigToRender(state.configOriginal)
    props.close()
  }

  return (
    <Dialog onClose={cancel} container={document.body}>
      <DialogHeader title="Widget Settings" />
      <DialogContent>
        <div className={styles.dialogContent}>
          <FormField
            name="widget"
            label="Widget"
            type="searchselect"
            placeholder="Search widgets..."
            noResultsLabel="No widgets found"
            options={selectableWidgetOptions}
            value={state.configInput.widget}
            onChange={updateConfigField}
          />
          <FormField
            name="id"
            label="Identifier for the widget's area. Should be unique if not empty."
            type="text"
            value={state.configInput.id}
            onChange={updateConfigField}
          />
          <FormField
            name="panelstyle"
            label="panel style"
            type="searchselect"
            placeholder="Search panel styles..."
            noResultsLabel="No styles found"
            options={[
              { label: 'Selecteer een style', value: '' },
              { label: 'standaard', value: 'standaard' },
              { label: 'no scrollBars', value: 'noScrollBars' },
              { label: 'toolbar', value: 'toolbar' },
              { label: 'border', value: 'border' },
              { label: 'border 2', value: 'border2' },
              { label: 'paper', value: 'paper' },
              { label: 'paper 2', value: 'paper2' },
              { label: 'padding', value: 'padding1' },
              { label: 'padding 2', value: 'padding2' },
              { label: 'padding 3', value: 'padding3' },
              { label: 'none', value: 'none' },
              { label: 'none white', value: 'noneWhite' },
              { label: 'refresh', value: 'refresh' },
              { label: 'three color template', value: 'threeColorTemplate' },
            ]}
            value={state.configInput.panelstyle}
            onChange={updateConfigField}
          />
          <FormField
            name="panelstyleExtra"
            label="panel style  (noScrollBars)"
            type="text"
            value={state.configInput.panelstyleExtra}
            onChange={updateConfigField}
          />
          <FormField
            name="titleMenubar"
            label="title menubar"
            type="text"
            value={state.configInput.titleMenubar}
            onChange={updateConfigField}
          />
          <FormField
            name="infoMenubar"
            label="Info popup menubar"
            type="text"
            value={state.configInput.infoMenubar}
            onChange={updateConfigField}
          />
          <FormField
            name="colorPrimaryAction"
            label='give primary action color (when panel style "three color template")'
            type="boolean"
            value={state.configInput.colorPrimaryAction}
            onChange={updateConfigField}
          />
          <FormGroup tag="fieldset" className={styles['fieldset']}>
            <legend>Behaviour on loading</legend>
            <FormField
              name="showLoadingOverlay"
              label="Show loading overlay while loading."
              type="boolean"
              value={state.configInput.showLoadingOverlay}
              onChange={updateConfigField}
            />
            <FormField
              name="syncWithUserActionsDisabled"
              label="User interaction disabled while other widgets are loading."
              type="boolean"
              value={state.configInput.syncWithUserActionsDisabled}
              onChange={updateConfigField}
            />
            <FormField
              name="voteForUserActionsDisabledWhenLoading"
              label="Widget blocks user interaction on other widgets while loading."
              type="boolean"
              value={state.configInput.voteForUserActionsDisabledWhenLoading}
              onChange={updateConfigField}
            />
          </FormGroup>
          <FormField
            name="hidden"
            label="hide this panel. Parameter must be true or false"
            type="text"
            topicValueInterpolationEnabled={true}
            value={state.configInput.hidden}
            onChange={updateConfigField}
          />
          <FormField
            name="fontFamily"
            label="CSS font-family definition for this widget, overrides default font"
            placeholder="'Exo 2 Variable', sans-serif"
            type="text"
            value={state.configInput.fontFamily}
            onChange={updateConfigField}
          />
          {shouldApplyBorderRadius(state.configInput.widget) && (
            <>
              <FormField
                name="finalTopLeftBorderRadius"
                label="final top-left border radius"
                type="text"
                value={state.configInput.finalTopLeftBorderRadius}
                onChange={updateConfigField}
              />
              <FormField
                name="finalTopRightBorderRadius"
                label="final border top-right radius"
                type="text"
                value={state.configInput.finalTopRightBorderRadius}
                onChange={updateConfigField}
              />
              <FormField
                name="finalBottomLeftBorderRadius"
                label="final bottom-left border radius"
                type="text"
                value={state.configInput.finalBottomLeftBorderRadius}
                onChange={updateConfigField}
              />
              <FormField
                name="finalBottomRightBorderRadius"
                label="final bottom-right border radius"
                type="text"
                value={state.configInput.finalBottomRightBorderRadius}
                onChange={updateConfigField}
              />
            </>
          )}
          <FormField
            name="hiddenwhenparametersareempty"
            label="comma separated parameters that need to have a value"
            type="text"
            value={state.configInput.hiddenwhenparametersareempty}
            onChange={updateConfigField}
          />
          <FormFields
            descriptions={descriptions}
            values={state.configInput}
            onChange={updateConfigField}
            localHistoryKeyPrefix={localHistoryKeyPrefix}
            setConfigFeedback={(variable, type, message) => setConfigFeedback(variable.name, type, message)}
          />
        </div>
      </DialogContent>

      <DialogFooter className="flex-col">
        {Boolean(Object.keys(state.feedback.error).length) && (
          <div className="p-2 rounded-md">{Object.values(state.feedback.error)}</div>
        )}
        <div className="flex justify-center gap-4 text-center flex-shrink-0">
          <Button color="secondary" style={{ minWidth: '120px' }} onClick={cancel}>
            Cancel
          </Button>
          <Button
            disabled={Boolean(Object.keys(state.feedback.error).length)}
            color="primary"
            style={{ minWidth: '120px' }}
            onClick={() => {
              delayedUpdateEnabled.current = false
              props.setConfigToRender(state.configInput)
              setState((s) => ({ ...s, waitingToSaveAndClose: true }))
            }}
          >
            OK
          </Button>
        </div>
      </DialogFooter>
    </Dialog>
  )
}

function useSelectableWidgetOptions(): {label: string, value: string}[] {
  const featureFlags = useFeatureFlags()
  return React.useMemo(() => {
    return allWidgetDefinitionsSorted
      .filter(function isAllowed(widget) {
        if (featureFlags.selectableWidgets.names?.allowed?.includes(widget.name)) return true
        if (featureFlags.selectableWidgets.names?.blocked?.includes(widget.name)) return false
        if ('category' in widget) {
          if (featureFlags.selectableWidgets.categories?.allowed?.includes(widget.category)) return true
          if (featureFlags.selectableWidgets.categories?.blocked?.includes(widget.category)) return false
        }
        return featureFlags.selectableWidgets.defaultPolicy !== 'blocked'
      })
      .map(w => ({label: w.label, value: w.name}))
  }, [featureFlags.selectableWidgets])
}

type DefWithDefaultQuery = { defaultQuery: string } & ConfigDescription<any, any, any>
export function mutateWidgetTypeWithDefaults<T extends WidgetConfig>(
  config: T,
  widget: T['widget'],
  widgetDefs: WidgetDescription<any, any, any>[] = allWidgetDefinitionsSorted
) {
  config.widget = widget

  const widgetDef = widgetDefs.find((w) => w.name === config.widget) as WidgetDescription<any, any, any>
  const defsWithDefaultQueries = widgetDef?.variables?.filter(
    (obj): obj is DefWithDefaultQuery =>
      (obj.type === 'yasgui' || obj.type === 'turtle') && 'defaultQuery' in obj && typeof obj.defaultQuery === 'string' && !!obj.defaultQuery
  )

  if (defsWithDefaultQueries) {
    for (const def of defsWithDefaultQueries) {
      const weakTypedConfig = config as unknown as Record<string, string>
      if (def.name in weakTypedConfig) continue
      weakTypedConfig[def.name] = def.defaultQuery
    }
  }

  if (
    widget === 'SplitLayout' || widget === 'SplitLayoutTest' ||
    widget === 'BBKVKProductTemplate' || widget === 'BB ButtonPopup'
  ) {
    const layoutConfig = config as any
    layoutConfig.direction = 'column'
    layoutConfig.areas = [original(layoutConfig)]
    if (widget === 'SplitLayoutTest') {
      layoutConfig.areaSizes = ['1', '1']
    } else {
      layoutConfig.areaSizes = ['200px', '1']
    }

  } else if (widget === 'Tab' || widget === 'BBSideMenu') {
    const configWithTabs = config as any
    configWithTabs.tabs = ['Tab 1', 'Tab 2']
    configWithTabs.areas = [original(config)]

  } else {
    const childConfigOfSameType =
      (config as any).areas &&
      (config as any).areas.find((area: WidgetConfig) => area && area.widget === widget)
    if (childConfigOfSameType) config = childConfigOfSameType
  }

  forEachEntry(DEFAULT_WIDGET_CONFIG.general, (key) => {
    if (!(key in config)) config[key] = DEFAULT_WIDGET_CONFIG.general[key]
  })

  const defaultConfig = DEFAULT_WIDGET_CONFIG[config.widget as keyof typeof DEFAULT_WIDGET_CONFIG]
  if (defaultConfig != null) {
    forEachEntry(defaultConfig, (key) => {
      if (!(key in config)) config[key] = defaultConfig[key]
    })
  }
}

export function shouldApplyBorderRadius(widgetName?: string) {
  if (!widgetName) return false
  return widgetName !== 'BBParameterLinkEmptyWidget' && !widgetName.includes('SplitLayout')
}
